import React from 'react';
import { GovIcon, GovMessage } from '@gov-design-system-ce/react';
import { useGovFormContext } from '@gov-design-system-ce/react-form-core';
import { MarkdownRender } from '@gov-nx/component/web';
import { useReferenceNumberFieldDisable } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import { hasOwnProperty } from '@gov-nx/core/types';
import { useDataBoxStoreLists } from '@gov-nx/store';
import type {
	CommonDataBoxSelectProps,
	DataBoxOnlyOptionProps,
	WebDataBoxDetailsProps,
	WebDataBoxSelectProps,
} from '@gov-nx/ui/types';
import { prepareDataBoxOptions } from '@gov-nx/utils/common';
import { ToggleButton } from '../../Button/ToggleButton';
import { FormWidthElementLayout } from '../../Layout/FormWidthElementLayout';
import { LayoutGap } from '../../Layout/LayoutGap';
import { FormInput } from '../FormInput';
import { FormSelect } from '../FormSelect';

const DataBoxDetails = (props: WebDataBoxDetailsProps) => {
	const referenceFieldDisable = useReferenceNumberFieldDisable();
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);

	return (
		<ToggleButton
			wcagShowLabel={t('data-box.wcag.zobrazit-doplnkove-udaje')}
			showLabel={t('data-box.labels.zobrazit-doplnkove-udaje')}
			hideLabel={t('data-box.labels.skryt-doplnkove-udaje')}
			className="toggle-bold">
			<LayoutGap gap="m">
				<MarkdownRender>
					{props.titleCopy ?? t('data-box.messages.doplnujici-udaje-obsag')}
				</MarkdownRender>
				<FormWidthElementLayout>
					<FormInput
						field={{
							name: props.fileNumberField?.name ?? 'naseCisloJednaci',
							placeholder:
								props.fileNumberField?.labelCopy ??
								t('data-box.placeholders.cislo-jednaci') ??
								undefined,
							isDisabled: props.fileNumberField?.isDisabled || props.disabled,
						}}
						label={{
							children:
								props.fileNumberField?.placeholderCopy ??
								t('data-box.fields.cislo-jednaci'),
						}}
					/>
				</FormWidthElementLayout>
				<FormWidthElementLayout>
					<FormInput
						field={{
							name: props.referenceNumberField?.name ?? 'naseSpisovaZnacka',
							placeholder:
								referenceFieldDisable.placeholderCopy ??
								props.referenceNumberField?.placeholderCopy ??
								t('data-box.placeholders.spisova-znacka'),
							isDisabled:
								referenceFieldDisable.isDisabled ||
								props.referenceNumberField?.isDisabled ||
								props.disabled,
						}}
						label={{
							children:
								props.referenceNumberField?.labelCopy ??
								t('data-box.fields.spisova-znacka'),
						}}
					/>
				</FormWidthElementLayout>
			</LayoutGap>
		</ToggleButton>
	);
};

const DataBoxOnlyOption = ({
	isRequired,
	label,
	option,
}: DataBoxOnlyOptionProps) => (
	<div className="flex flex-col gap-2">
		<span className="text-body-s!">
			{label.children}
			{isRequired ? (
				<span className="gov-color--text-status-error"> *</span>
			) : null}
		</span>
		<span className="font-semibold">{option.label}</span>
	</div>
);

const isOldProps = (
	props: CommonDataBoxSelectProps
): props is WebDataBoxSelectProps => hasOwnProperty(props, 'types');

export const DataBoxSelect = (props: CommonDataBoxSelectProps) => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const { isRequired } = useGovFormContext().propsFromSchema(props.field.name);
	const { getConnectedDataBoxesByTypes } = useDataBoxStoreLists();

	const dataBoxesOptions = isOldProps(props)
		? prepareDataBoxOptions(getConnectedDataBoxesByTypes(props.types))
		: props.field.options;

	if (dataBoxesOptions.length === 0) {
		return (
			<GovMessage
				color="warning"
				type="subtle"
				icon={
					<GovIcon
						type="components"
						name="exclamation-triangle-fill"
					/>
				}>
				<MarkdownRender>
					{t('data-box.messages.nemate-datovou-schranku')}
				</MarkdownRender>
			</GovMessage>
		);
	}

	return (
		<fieldset>
			<legend className="sr-only">
				{t('data-box.wcag.vyber-datove-schranky')}
			</legend>
			{dataBoxesOptions.length === 1 ? (
				<DataBoxOnlyOption
					isRequired={isRequired}
					label={props.label}
					option={dataBoxesOptions[0]}
				/>
			) : (
				<FormWidthElementLayout type="xl">
					<FormSelect
						{...props}
						field={{
							...props.field,
							children: (
								<>
									{dataBoxesOptions.map((o) => (
										<option
											value={o.value}
											key={o.value}>
											{o.label}
										</option>
									))}
								</>
							),
						}}
					/>
				</FormWidthElementLayout>
			)}
			{!props.isDetailDisabled && (
				<DataBoxDetails
					{...props.details}
					disabled={props.field.isDisabled}
				/>
			)}
		</fieldset>
	);
};
